import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, Brain, X, Lightbulb, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { isFillBlankCorrect, normalizeAnswer } from '../utils/fillBlankChecker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuizSkillBuilder() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [weakAreas, setWeakAreas] = useState([]);
  const [mode, setMode] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);

  const fetchSkillBuilderQuiz = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quiz/skill-builder`, {
        params: { limit: 10 },
        withCredentials: true
      });
      setQuestions(response.data.questions);
      setMode(response.data.mode);
      setWeakAreas(response.data.weak_areas || []);
      setQuestionStartTime(Date.now());
    } catch (error) {
      toast.error('Failed to load skill builder quiz');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSkillBuilderQuiz();
  }, [fetchSkillBuilderQuiz]);

  const handleAnswer = (answer) => {
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setAnswers({
      ...answers,
      [questions[currentIndex].question_id]: {
        answer,
        time_spent: timeSpent
      }
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setQuestionStartTime(Date.now());
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setQuestionStartTime(Date.now());
    }
  };

  const handleSubmit = async () => {
    try {
      const submissions = Object.entries(answers).map(([question_id, data]) => ({
        question_id,
        selected_answer: data.answer,
        time_spent: data.time_spent
      }));

      const response = await axios.post(
        `${BACKEND_URL}/api/quiz/submit`,
        submissions,
        { withCredentials: true }
      );

      navigate('/results', { 
        state: { 
          results: response.data.results, 
          topic: 'Skill Builder',
          mode: mode,
          weakAreas: weakAreas
        } 
      });
    } catch (error) {
      toast.error('Failed to submit quiz');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion?.question_id]?.answer;
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Button
            data-testid="skill-builder-back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-accent" />
            <h1 className="text-2xl font-bold text-slate-900 font-sans">Skill Builder Mode</h1>
          </div>
          {mode === 'adaptive' && weakAreas.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-slate-600">Focusing on your weak areas:</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {weakAreas.map((area, idx) => (
                  <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-bold tracking-wide uppercase">
                    {area.subtopic} ({area.accuracy.toFixed(0)}%)
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-accent h-2">
        <div
          className="bg-primary h-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-6">
          <div className="text-sm text-slate-500 mb-2">
            Question {currentIndex + 1} of {questions.length}
          </div>
          <div className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-bold tracking-wide uppercase inline-block">
            {currentQuestion?.subtopic}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 mb-8 leading-relaxed">
            {currentQuestion?.question_text}
          </h2>

          {currentQuestion?.question_type === 'multiple_choice' ? (
            <div className="space-y-3">
              {currentQuestion.options?.map((option, idx) => (
                <button
                  key={idx}
                  data-testid={`skill-builder-option-${idx}`}
                  onClick={() => handleAnswer(option)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    currentAnswer === option
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      currentAnswer === option
                        ? 'border-primary bg-primary'
                        : 'border-slate-300'
                    }`}>
                      {currentAnswer === option && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-base text-slate-900">{option}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <input
              type="text"
              data-testid="skill-builder-fill-in-blank-input"
              value={currentAnswer || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder="Type your answer here..."
              className="w-full h-12 bg-slate-50 border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg px-4 transition-all text-base"
            />
          )}
        </div>

        <div className="flex justify-between items-center">
          <Button
            data-testid="skill-builder-previous-button"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            variant="outline"
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Previous
          </Button>

          <div className="text-sm text-slate-500">
            {Object.keys(answers).length} of {questions.length} answered
          </div>

          {currentIndex === questions.length - 1 ? (
            <Button
              data-testid="skill-builder-submit-button"
              onClick={handleSubmit}
              disabled={Object.keys(answers).length !== questions.length}
              className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-semibold shadow-lg shadow-accent/20 transition-all hover:-translate-y-0.5"
            >
              Submit Quiz
            </Button>
          ) : (
            <Button
              data-testid="skill-builder-next-button"
              onClick={handleNext}
              className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-semibold shadow-lg shadow-accent/20 transition-all hover:-translate-y-0.5"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}